import React, { useMemo } from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaBitcoin,
  FaChartLine,
  FaCube,
  FaExchangeAlt,
  FaFish,
  FaTrophy,
  FaWallet,
} from "react-icons/fa";

export default function TopAddressesPanel({ blocks }) {
  const { rows, allAddressTotal, blockStats, largestRow, transferGroups, exchangeTransfers, totalTransferVolume } = useMemo(() => {
    const blockList = Array.isArray(blocks) ? blocks : [];
    const addressBalances = new Map();
    const addressDirections = new Map();
    const transferMap = new Map();
    const exchangeTransferList = [];
    let transferVolume = 0;

    blockList.forEach((block) => {
      const topAddresses = Array.isArray(block?.topAddresses) ? block.topAddresses : [];
      topAddresses.forEach((item) => {
        const address = String(item.address || "");
        const balance = Number(item.count || 0);
        if (!address || !Number.isFinite(balance)) return;
        addressBalances.set(address, (addressBalances.get(address) || 0) + balance);
      });

      const transactions = Array.isArray(block?.transactions) ? block.transactions : [];
      transactions.forEach((tx) => {
        const amount = Number(tx?.amountBtc || 0);
        if (!Number.isFinite(amount) || amount <= 0) return;
        const direction = tx?.transferDirection || "neutral";
        const label = tx?.transferLabel || "wallet -> wallet";
        const key = `${direction}:${label}`;
        const current = transferMap.get(key) || { direction, label, amount: 0, count: 0 };
        current.amount += amount;
        current.count += 1;
        transferMap.set(key, current);
        transferVolume += amount;

        if (direction === "inflow" || direction === "outflow" || direction === "exchange_transfer") {
          const fromAddresses = Array.isArray(tx?.fromAddresses) ? tx.fromAddresses.filter(Boolean) : [];
          const toAddress = String(tx?.toAddress || "");
          const primaryAddress = direction === "outflow" ? (fromAddresses[0] || toAddress) : toAddress;
          exchangeTransferList.push({
            blockHeight: Number(block?.blockHeight),
            amount,
            direction,
            label,
            address: primaryAddress,
            fromAddresses,
            toAddress,
          });
        }

        const toAddress = String(tx?.toAddress || "");
        if (toAddress) {
          const existing = addressDirections.get(toAddress) || { direction: "neutral", label: "wallet -> wallet", amount: 0 };
          if (amount > existing.amount) {
            addressDirections.set(toAddress, { direction, label, amount });
          }
        }
      });
    });

    const allTotal = Array.from(addressBalances.values()).reduce((sum, balance) => sum + balance, 0);
    const topAddresses = Array.from(addressBalances.entries())
      .map(([address, count]) => ({ address, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const computedRows = topAddresses.map((item, index) => {
      const balance = Number(item.count || 0);
      const transfer = addressDirections.get(item.address) || null;
      return {
        address: item.address,
        balance,
        percent: allTotal > 0 ? (balance / allTotal) * 100 : 0,
        rank: index + 1,
        transferDirection: transfer?.direction || null,
        transferLabel: transfer?.label || null,
      };
    });

    const blockHeights = blockList.map((block) => Number(block.blockHeight)).filter(Number.isFinite);
    const startHeight = blockHeights.length ? Math.min(...blockHeights) : null;
    const endHeight = blockHeights.length ? Math.max(...blockHeights) : null;

    return {
      rows: computedRows,
      allAddressTotal: allTotal,
      blockStats: {
        count: blockList.length,
        startHeight,
        endHeight,
      },
      largestRow: computedRows.reduce((largest, row) => (!largest || row.balance > largest.balance ? row : largest), null),
      transferGroups: Array.from(transferMap.values()).sort((a, b) => b.amount - a.amount).slice(0, 4),
      exchangeTransfers: exchangeTransferList.sort((a, b) => b.amount - a.amount).slice(0, 8),
      totalTransferVolume: transferVolume,
    };
  }, [blocks]);

  const top10Total = rows.reduce((sum, row) => sum + row.balance, 0);
  const top10Percent = allAddressTotal > 0 ? (top10Total / allAddressTotal) * 100 : 0;
  const maxPercent = rows.reduce((max, row) => Math.max(max, row.percent), 0) || 1;

  const compactAddress = (address) => {
    const value = String(address || "");
    if (value.length <= 18) return value;
    return `${value.slice(0, 10)}...${value.slice(-4)}`;
  };

  const formatBtc = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDirectionLabel = (label) =>
    String(label || "wallet -> wallet")
      .replace(/\s*->\s*/g, "→")
      .toUpperCase();

  const rangeLabel =
    blockStats.startHeight != null && blockStats.endHeight != null
      ? `${blockStats.startHeight} – ${blockStats.endHeight}`
      : "--";

  const displayDate = useMemo(
    () => new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date()),
    []
  );

  const transferIcon = (direction) => {
    if (direction === "inflow") return <FaArrowDown />;
    if (direction === "outflow") return <FaArrowUp />;
    if (direction === "exchange_transfer") return <FaExchangeAlt />;
    return <FaWallet />;
  };

  return (
    <div className="top-addresses-panel">
      <header className="smart-money-hero">
        <div className="smart-money-brand">
          <div className="bitcoin-medallion"><FaBitcoin /></div>
          <div>
            <h2>
              <span>SMART MONEY</span>
              <strong>ON THE MOVE</strong>
            </h2>
            <p className="whale-overview-copy">
              <span className="whale-rule"><FaFish /> Whale <em className="whale-threshold">&gt; 100 BTC counted</em></span>
              <span className="whale-overview-label">Addresses Overview</span>
            </p>
          </div>
        </div>

        <div className="block-range-card">
          <span>BLOCK RANGE</span>
          <strong>{rangeLabel}</strong>
          <em>TOTAL BLOCKS: {blockStats.count || "--"}</em>
          <em className="block-range-date">{displayDate}</em>
        </div>
      </header>

      {transferGroups.length > 0 && (
        <section className="transfer-label-panel">
          <div className="transfer-label-heading">TRANSFER LABELS</div>
          <div className="transfer-label-grid">
            {transferGroups.map((group) => (
              <div key={`${group.direction}-${group.label}`} className={`transfer-label-card ${group.direction}`}>
                <div className="transfer-label-icon">{transferIcon(group.direction)}</div>
                <div className="transfer-label-copy">
                  <span>{group.label}</span>
                  <strong>{formatBtc(group.amount)} BTC</strong>
                  <em>{group.count} TRANSACTIONS</em>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {exchangeTransfers.length > 0 && (
        <section className="exchange-transfer-panel">
          <div className="exchange-transfer-heading">
            <span><FaExchangeAlt /> EXCHANGE TRANSFERS</span>
            <strong>{exchangeTransfers.length} HIGHLIGHTS</strong>
          </div>
          <div className="exchange-transfer-list">
            {exchangeTransfers.map((transfer, index) => (
              <div key={`${transfer.blockHeight}-${transfer.address}-${index}`} className={`exchange-transfer-row ${transfer.direction}`}>
                <span className="exchange-transfer-direction">
                  {transferIcon(transfer.direction)}
                  <em>{formatDirectionLabel(transfer.label)}</em>
                </span>
                <span className="exchange-transfer-address" title={transfer.address}>{compactAddress(transfer.address)}</span>
                <strong>{formatBtc(transfer.amount)} BTC</strong>
                <b>#{Number.isFinite(transfer.blockHeight) ? transfer.blockHeight : "--"}</b>
              </div>
            ))}
          </div>
        </section>
      )}

      <main className="top-addresses-card">
        <div className="top-addresses-title">
          <FaTrophy />
          <span>TOP 10 ADDRESSES BY TOTAL BTC</span>
        </div>

        <div className="addresses-table-head">
          <span>#</span>
          <span>ADDRESS</span>
          <span>TOTAL BTC</span>
          <span>% OF TOTAL</span>
          <span className="direction-head">DIRECTION</span>
        </div>

        <div className="addresses-table-body">
          {rows.map((row) => (
            <div key={`${row.address}-${row.rank}`} className="addresses-table-row">
              <span className="rank-number">{row.rank}</span>
              <span className="address-value" title={row.address}>{compactAddress(row.address)}</span>
              <span className="btc-value">{formatBtc(row.balance)}</span>
              <span className="percent-visual">
                <span
                  className="percent-fill"
                  style={{ width: `${Math.max(6, Math.min(100, (row.percent / maxPercent) * 100))}%` }}
                />
              </span>
              <span className="percent-value">{row.percent.toFixed(2)}%</span>
              <span className={`direction-value ${row.transferDirection || "none"}`} title={row.transferLabel || ""}>
                {row.transferDirection ? transferIcon(row.transferDirection) : null}
                <em>{row.transferLabel ? formatDirectionLabel(row.transferLabel) : "--"}</em>
              </span>
            </div>
          ))}
        </div>

        <div className="addresses-total-row">
          <span>TOTAL (TOP 10)</span>
          <strong>{formatBtc(top10Total)} BTC</strong>
          <em>{top10Percent.toFixed(2)}%</em>
          <b>TOP 10 SHARE</b>
        </div>
      </main>

      <section className="smart-money-bottom">
        <div className="key-stats-card">
          <h3>KEY STATS</h3>
          <div className="key-stats-grid">
            <div>
              <FaBitcoin className="stat-orange" />
              <span>TOTAL<br />BTC</span>
              <strong>{formatBtc(allAddressTotal)}</strong>
              <em>Tracked addresses</em>
            </div>
            <div>
              <FaCube className="stat-green" />
              <span>LARGEST<br />ADDRESS</span>
              <strong>{largestRow ? formatBtc(largestRow.balance) : "--"}</strong>
              <em>{largestRow ? `Rank #${largestRow.rank}` : "No data"}</em>
            </div>
            <div>
              <FaChartLine className="stat-cyan" />
              <span>ACTIVE<br />BLOCKS</span>
              <strong>{blockStats.count || "--"}</strong>
              <em>{rangeLabel}</em>
            </div>
            <div>
              <FaExchangeAlt className="stat-cyan" />
              <span>TRANSFER<br />VOLUME</span>
              <strong>{formatBtc(totalTransferVolume)}</strong>
              <em>Labelled BTC</em>
            </div>
          </div>
        </div>

        <div className="market-signal-card">
          <h3>MARKET SIGNAL</h3>
          <strong><FaFish /> ADDRESS CONCENTRATION</strong>
          <ul>
            <li>Top 10 addresses hold {formatBtc(top10Total)} BTC in the current data window</li>
            <li>Largest address contributes {largestRow ? largestRow.percent.toFixed(2) : "0.00"}% of tracked total</li>
            <li>Transfer labels summarize exchange / wallet movement above the table</li>
          </ul>
        </div>
      </section>

      <footer className="smart-money-footer">
        <span><FaCube /> Block Insight</span>
        <em>DATA SOURCE: LATEST UTXO SNAPSHOT</em>
        <strong>TRACKING WHALES. <b>FINDING SIGNALS.</b></strong>
      </footer>
    </div>
  );
}
